// Comisiones pagadas — Portal Admin VentasLuga.
// Mockup: surekeep/mockups/backoffice/portal-admin-vl-com-pagadas.html
//
// Por vendedor, por comisión, por tienda y por producto; con permiso, Por pagar y Estados de
// cuenta. La pestaña, el periodo y el vendedor van en la URL (`?tab=`, `?periodo=`, `?vendedor=`):
// "Esta semana" es el corte en curso y los demás periodos suman los cortes cerrados más el corte en
// curso.
import { useEffect, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Button, usePermissions } from '@/kit';
import { sweepWriteDoubts } from '@/lib/writeDoubt';
import { PageTabs, type PageTab } from '@/shared/components/PageTabs';
import { ScreenFrame, ScreenFrameBar } from '@/shared/patterns/ScreenFrame';
import { usePaymentAttention } from './api/commissionQueries';
import { useCommissionDirectory } from './api/useCommissionDirectory';
import { COMMISSION_PERMISSIONS } from './lib/commissionPermissions';
import {
    DEFAULT_PAYOUT_PERIOD,
    localToday,
    parsePayoutPeriod,
    type PayoutPeriod,
} from './lib/payoutPeriods';
import { ExportCsvModal } from './components/payouts/ExportCsvModal';
import { PaymentsDueTab } from './components/payouts/PaymentsDueTab';
import { ProductsPaidTab } from './components/payouts/ProductsPaidTab';
import { RulesPaidTab } from './components/payouts/RulesPaidTab';
import { SellersTab } from './components/payouts/SellersTab';
import { StoresPaidTab } from './components/payouts/StoresPaidTab';
import { StatementsExplorer } from './components/statements/StatementsExplorer';

type PaidTab = 'vendedor' | 'comision' | 'tienda' | 'producto' | 'por-pagar' | 'estados';

const PAID_TABS: readonly PageTab<PaidTab>[] = [
    { key: 'vendedor', label: 'Por vendedor' },
    { key: 'comision', label: 'Por comisión' },
    { key: 'tienda', label: 'Por tienda' },
    { key: 'producto', label: 'Por producto' },
];

/** Las pestañas que el rol puede ver; "Por pagar" cuenta pendientes y depósitos por revisar. */
function tabsFor(
    canPay: boolean,
    canStatements: boolean,
    dueCount: number | undefined,
): PageTab<PaidTab>[] {
    const tabs = [...PAID_TABS];
    if (canPay)
        tabs.push({
            key: 'por-pagar',
            label: 'Por pagar',
            count: dueCount,
            countTitle: 'Pendientes y depósitos por revisar',
        });
    if (canStatements) tabs.push({ key: 'estados', label: 'Estados de cuenta' });
    return tabs;
}

/** El CSV de nómina es de lo liquidado: no aplica a Por pagar ni a los estados de cuenta. */
const PAYROLL_TABS: readonly PaidTab[] = ['vendedor', 'comision', 'tienda', 'producto'];

function parsePaidTab(raw: string | undefined, tabs: readonly PageTab<PaidTab>[]): PaidTab {
    return tabs.find((tab) => tab.key === raw)?.key ?? 'vendedor';
}

export function ComisionesPagarPage() {
    const navigate = useNavigate();
    // Las dudas de "pagado por fuera" y de los ajustes que ya caducaron no se quedan en el navegador.
    useEffect(() => {
        sweepWriteDoubts();
    }, []);
    const {
        tab: rawTab,
        periodo,
        vendedor,
    } = useSearch({ from: '/_backoffice/retail-admin/$section' });
    const { hasPermission } = usePermissions();
    const canPay = hasPermission(COMMISSION_PERMISSIONS.paymentRead);
    const attention = usePaymentAttention(canPay);
    const dueCount = attention.data
        ? attention.data.pending.length + attention.data.unverified.length
        : undefined;
    const tabs = tabsFor(canPay, hasPermission(COMMISSION_PERMISSIONS.statementRead), dueCount);
    const tab = parsePaidTab(rawTab, tabs);
    const period = parsePayoutPeriod(periodo);
    const directory = useCommissionDirectory();
    const [exporting, setExporting] = useState(false);
    const today = localToday();

    const patch = (next: { tab?: string; periodo?: string; vendedor?: string }) => {
        void navigate({
            to: '/retail-admin/$section',
            params: { section: 'comisiones' },
            search: (prev) => ({ ...prev, ...next }),
            replace: true,
        });
    };

    const shared = {
        period,
        onPeriodChange: (next: PayoutPeriod) => {
            patch({ periodo: next === DEFAULT_PAYOUT_PERIOD ? undefined : next });
        },
        today,
        directory,
    };

    return (
        <ScreenFrame>
            <ScreenFrameBar className="items-center justify-between gap-2 px-3 py-0">
                <PageTabs
                    tabs={tabs}
                    active={tab}
                    onChange={(key) => {
                        patch({ tab: key === 'vendedor' ? undefined : key });
                    }}
                    className="border-b-0 bg-transparent px-0"
                />
                {hasPermission(COMMISSION_PERMISSIONS.payoutRead) && PAYROLL_TABS.includes(tab) && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                            setExporting(true);
                        }}
                    >
                        CSV
                    </Button>
                )}
            </ScreenFrameBar>
            {tab === 'vendedor' && (
                <SellersTab
                    {...shared}
                    sellerId={vendedor ?? null}
                    onSelect={(sellerId) => {
                        patch({ vendedor: sellerId });
                    }}
                />
            )}
            {tab === 'comision' && <RulesPaidTab {...shared} />}
            {tab === 'tienda' && <StoresPaidTab {...shared} />}
            {tab === 'producto' && <ProductsPaidTab {...shared} />}
            {tab === 'por-pagar' && <PaymentsDueTab directory={directory} />}
            {tab === 'estados' && (
                <StatementsExplorer retailerId={null} sellerName={directory.sellerName} />
            )}
            {exporting && (
                <ExportCsvModal
                    onClose={() => {
                        setExporting(false);
                    }}
                />
            )}
        </ScreenFrame>
    );
}
